import * as pdfjs from "pdfjs-dist";
import PdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?worker";
import type { LocalProject, PageLayout } from "../types";
import {
  buildPageLayoutFromItems,
  type RawTextItem
} from "./layout";

export {
  buildPageLayoutFromItems,
  groupItemsIntoLines,
  groupLinesIntoBlocks
} from "./layout";
export type { RawTextItem, TextLine } from "./layout";

if (typeof Worker !== "undefined") {
  pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker();
}

export type PdfDocument = Awaited<
  ReturnType<typeof pdfjs.getDocument>["promise"]
>;

export interface ParseProgress {
  currentPage: number;
  pageCount: number;
}

export async function extractPageLayout(
  document: PdfDocument,
  pageNumber: number
): Promise<PageLayout> {
  const page = await document.getPage(pageNumber);
  const viewport = page.getViewport({ scale: 1 });
  const content = await page.getTextContent();
  const items: RawTextItem[] = [];

  content.items.forEach((rawItem, order) => {
    if (!("str" in rawItem) || !rawItem.str.trim()) return;
    const transform = pdfjs.Util.transform(viewport.transform, rawItem.transform);

    // Rotated labels such as an arXiv margin stamp are not part of the paper's
    // horizontal reading flow. Keeping them would create a tall false paragraph.
    if (Math.abs(transform[1]) > Math.abs(transform[0]) * 0.65) return;

    const fontSize = Math.max(
      Math.hypot(transform[2], transform[3]),
      rawItem.height || 1
    );
    const width = Math.max(rawItem.width * viewport.scale, 1);
    const height = Math.max(fontSize, rawItem.height * viewport.scale, 1);
    items.push({
      text: rawItem.str,
      bbox: {
        x: transform[4],
        y: transform[5] - height,
        width,
        height
      },
      fontSize,
      order,
      hasEol: rawItem.hasEOL
    });
  });

  return buildPageLayoutFromItems(
    items,
    pageNumber,
    viewport.width,
    viewport.height
  );
}

export async function loadPdfDocument(
  source: ArrayBuffer | Uint8Array
): Promise<PdfDocument> {
  return pdfjs.getDocument({ data: source }).promise;
}

export async function parsePdf(
  file: File,
  onProgress?: (progress: ParseProgress) => void
): Promise<LocalProject> {
  if (
    file.type !== "application/pdf" &&
    !file.name.toLowerCase().endsWith(".pdf")
  ) {
    throw new Error("请选择 PDF 文件。");
  }
  if (file.size > 80 * 1024 * 1024) {
    throw new Error("当前版本建议使用 80 MB 以内的论文 PDF。");
  }

  const bytes = await file.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", bytes.slice(0));
  const id = Array.from(new Uint8Array(hash))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
  const document = await loadPdfDocument(bytes.slice(0));
  const layout: PageLayout[] = [];

  for (
    let pageNumber = 1;
    pageNumber <= document.numPages;
    pageNumber += 1
  ) {
    layout.push(await extractPageLayout(document, pageNumber));
    onProgress?.({ currentPage: pageNumber, pageCount: document.numPages });
  }

  let referencesStarted = false;
  for (const page of layout) {
    for (const block of page.blocks) {
      if (/^references\b/i.test(block.text)) {
        referencesStarted = true;
        block.role = "reference";
        block.translatable = false;
        continue;
      }
      if (referencesStarted) {
        block.role = "reference";
        block.translatable = false;
      }
    }
  }

  const meaningfulCharacters = layout
    .flatMap((page) => page.blocks)
    .reduce(
      (sum, block) => sum + block.text.replace(/\s/g, "").length,
      0
    );
  if (meaningfulCharacters < Math.max(30, document.numPages * 15)) {
    throw new Error(
      "没有检测到可用文本层。这可能是扫描版 PDF，当前版本暂不支持 OCR。"
    );
  }

  const now = Date.now();
  return {
    layoutVersion: 2,
    id,
    name: file.name,
    fileSize: file.size,
    pageCount: document.numPages,
    createdAt: now,
    updatedAt: now,
    pdf: new Blob([bytes], { type: "application/pdf" }),
    layout,
    translations: {},
    settings: {
      currentPage: 1,
      zoom: 1,
      syncScroll: true
    }
  };
}
